import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetEmspUsersInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/emsp-users-invoices/get-emsp-users-invoices-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useEmspBoGetInvoicesQuery } from '@app/core/service/emsp-bo-invoice.service';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { EmspBoInvoiceListFilterSearchController } from '../emsp-bo-invoice-list-filter-search/emsp-bo-invoice-list-filter-search.controller';
import { EmspBoInvoiceListFilterStateController } from '../emsp-bo-invoice-list-filter-state/emsp-bo-invoice-list-filter-state.controller';
import { EmspBoInvoiceListFilterController } from '../emsp-bo-invoice-list-filter/emsp-bo-invoice-list-filter.controller';
import { EmspBoInvoiceListOptionsController } from '../emsp-bo-invoice-list-options/emsp-bo-invoice-list-options.controller';
import {
  EmspBoInvoiceListContextProvider,
  useEmspBoInvoiceListContext,
} from '../emsp-bo-invoice.context';
import {
  EMSP_BO_INVOICE_LIST_FILTER_NAME_LIST,
  EMSP_BO_INVOICE_LIST_SORT_FIELD,
} from './emsp-bo-invoice-list.config';
import * as css from './emsp-bo-invoice-list.module.scss';
import { EmspBoInvoiceListView } from './emsp-bo-invoice-list.view';

export const EmspBoInvoiceListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useEmspBoInvoiceListContext();

  // const [searchParams] = useSearchParams();

  // USER PERMISSIONS ///////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING //////////////////////////////////////////////////////////////

  const queryState = useEmspBoGetInvoicesQuery(getPaginatedQueryParameters);

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // TODO: Should be moved in filter-search component
  //   if (debouncedTextToSearch && debouncedTextToSearch.trim().length > 0) {
  //     params.textToSearch = debouncedTextToSearch.trim() || '';
  //   }

  // TODO: Should be move in dedicated component
  const filterNameActiveList = EMSP_BO_INVOICE_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filterActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ): void => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? EMSP_BO_INVOICE_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetEmspUsersInvoicesPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: EmspBoInvoiceListViewProtected, status: resourceStatus } =
    withProtectedResource(EmspBoInvoiceListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.EMSP_BO,
          action: ActionsByResource.EMSP_BO.READ_INVOICE,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id:
            getPaginatedQueryParameters.sortField ??
            EMSP_BO_INVOICE_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <EmspBoInvoiceListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <EmspBoInvoiceListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <EmspBoInvoiceListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filterActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filterActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>
        {EmspBoInvoiceListViewProtected}
      </Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Dialog
          open={actionFilterIsDialogOpen}
          onClose={() => setActionFilterIsDialogOpen(false)}
          position={DialogPosition.RIGHT}>
          <Dialog.Header
            icon="filter-funnel-01"
            title={t('$.emsp-bo-invoice-list-filter.title')}
            onClose={() => setActionFilterIsDialogOpen(false)}
          />
          <Dialog.Content>
            <EmspBoInvoiceListFilterController
              onCancel={() => setActionFilterIsDialogOpen(false)}
              onSuccess={() => setActionFilterIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </Stack>
  );
};

export const EmspBoInvoiceListControllerWithContext = (): React.JSX.Element => {
  return (
    <EmspBoInvoiceListContextProvider>
      <EmspBoInvoiceListController />
    </EmspBoInvoiceListContextProvider>
  );
};
